// Demo-only mockup of the incident record itself, shown when clicking the
// "Major incident declared" pipeline stage — the trigger, classification,
// and initial facts that kicked off the fan-out, so the pipeline's lead
// stage is inspectable the same way every downstream channel step already
// is. Fully local/decorative: no backend call, no real incident record.
import '../styles/incident-declared-mockup.css';
import { triggerLogLine, type FanoutIncident } from '../lib/outageFanout';

function IcoTriangle() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3.5 21.5 20h-19L12 3.5z" />
      <path d="M12 9.5v5" />
      <circle cx="12" cy="17.2" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}
function IcoClose() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
      <path d="M3.5 3.5l9 9M12.5 3.5l-9 9" strokeLinecap="round" />
    </svg>
  );
}

function incidentNumber(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return `INC-${String((h % 90000) + 10000)}`;
}

function formatDeclaredAt(iso: string | null | undefined): string {
  const d = iso ? new Date(iso) : new Date();
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export default function IncidentDeclaredMockup({
  incident,
  onClose,
}: {
  incident: FanoutIncident;
  onClose: () => void;
}) {
  const number = incidentNumber(String(incident.id ?? '') + (incident.declared_at || ''));
  const title = incident.impact_statement || incident.service_name || 'Major service disruption detected';
  const trigger = triggerLogLine(incident).text;
  const severity = incident.severity || 'critical';
  const usersLabel =
    typeof incident.estimated_users_impacted === 'number'
      ? incident.estimated_users_impacted.toLocaleString()
      : null;

  return (
    <div className="declared-mock-overlay" onClick={onClose}>
      <div className="declared-mock-card" onClick={(e) => e.stopPropagation()}>
        <div className="declared-mock-head">
          <span className="declared-mock-badge">
            <IcoTriangle /> Major Incident
          </span>
          <button className="declared-mock-close" onClick={onClose} aria-label="Close" title="Close">
            <IcoClose />
          </button>
        </div>

        <div className="declared-mock-body">
          <div className="declared-mock-idline">{number} &middot; Declared {formatDeclaredAt(incident.declared_at)}</div>
          <h2 className="declared-mock-title">{title}</h2>

          <div className="declared-mock-trigger">
            <span className="declared-mock-trigger-label">Trigger</span>
            <span className="declared-mock-trigger-text">{trigger}</span>
          </div>

          <div className="declared-mock-meta">
            <div className="declared-mock-meta-item">
              <span className="declared-mock-meta-label">Severity</span>
              <span className="declared-mock-meta-value declared-mock-sev">{severity}</span>
            </div>
            <div className="declared-mock-meta-item">
              <span className="declared-mock-meta-label">Status</span>
              <span className="declared-mock-meta-value">{incident.current_status || 'investigating'}</span>
            </div>
            <div className="declared-mock-meta-item">
              <span className="declared-mock-meta-label">Service</span>
              <span className="declared-mock-meta-value">{incident.service_name || 'Unnamed Service'}</span>
            </div>
            {incident.affected_site && (
              <div className="declared-mock-meta-item">
                <span className="declared-mock-meta-label">Site</span>
                <span className="declared-mock-meta-value">{incident.affected_site}</span>
              </div>
            )}
            {usersLabel && (
              <div className="declared-mock-meta-item">
                <span className="declared-mock-meta-label">Users Impacted</span>
                <span className="declared-mock-meta-value">{usersLabel}</span>
              </div>
            )}
            <div className="declared-mock-meta-item">
              <span className="declared-mock-meta-label">Declared By</span>
              <span className="declared-mock-meta-value">Atlas Incident Bot</span>
            </div>
          </div>

          {incident.workaround && (
            <div className="declared-mock-workaround">
              <span className="declared-mock-workaround-label">Workaround</span>
              <span className="declared-mock-workaround-text">{incident.workaround}</span>
            </div>
          )}

          <p className="declared-mock-note">
            Declaring a major incident automatically pages on-call, posts to Microsoft Teams,
            opens a ServiceNow ticket, drafts an AI incident report, and emails stakeholders —
            the steps that follow on this pipeline.
          </p>
        </div>
      </div>
    </div>
  );
}
